// Store links that carry the ad campaign code, so App Store Connect and
// Play Console can count installs per campaign.
//
// The campaign code is utm_campaign from the page address (for example
// tg26-ii-meta). It is remembered for the rest of the visit in
// sessionStorage, so store buttons on later pages still carry it. A visit
// that did not come from an ad keeps each page's own store links.
//
// These links must match the ones the campaign editor builds (see its
// README). For tg26-ii-meta:
//   https://apps.apple.com/app/apple-store/id6504144806?pt=126766867&ct=tg26-ii-meta&mt=8
//   https://play.google.com/store/apps/details?id=com.famring.membox&referrer=utm_source%3Dfamilyring.io%26utm_medium%3Dweb%26utm_campaign%3Dtg26-ii-meta%26utm_content%3Dii

export const APP_STORE_ID = '6504144806';
export const PROVIDER_TOKEN = '126766867';
export const PLAY_PACKAGE = 'com.famring.membox';

// Feature codes used in campaign names: in-person interview, interview from
// anywhere (is), games, birthdays.
export const FEATURE_CODES = ['ii', 'is', 'g', 'b'];

// Apple allows campaign tokens up to 30 characters.
const CODE = /^[a-z0-9][a-z0-9._-]{0,29}$/i;
export const STORAGE_KEY = 'familyring.campaign';

function sessionStore() {
  try {
    return window.sessionStorage;
  } catch (e) {
    return null;
  }
}

export function isValidCode(code) {
  return typeof code === 'string' && CODE.test(code);
}

export function campaignFromSearch(search) {
  const code = (new URLSearchParams(search || '').get('utm_campaign') || '').trim();
  return isValidCode(code) ? code : null;
}

export function rememberCampaign(code, storage = sessionStore()) {
  try {
    if (storage && isValidCode(code)) storage.setItem(STORAGE_KEY, code);
  } catch (e) {
    // Private browsing or blocked storage: the current page still works.
  }
}

export function recallCampaign(storage = sessionStore()) {
  try {
    const code = storage ? storage.getItem(STORAGE_KEY) : null;
    return isValidCode(code) ? code : null;
  } catch (e) {
    return null;
  }
}

// Remember utm_campaign from the page address, if it has a valid one.
// Call this from an effect or on load, not during render.
export function rememberCampaignFromSearch(search, storage = sessionStore()) {
  const code = campaignFromSearch(search);
  if (code) rememberCampaign(code, storage);
  return code;
}

export function featureOf(code, fallback) {
  const part = String(code).split('-')[1];
  return FEATURE_CODES.includes(part) ? part : fallback;
}

export function appStoreUrl(code, productPageId) {
  const params = [
    `pt=${encodeURIComponent(PROVIDER_TOKEN)}`,
    `ct=${encodeURIComponent(code)}`,
    'mt=8'
  ];
  if (productPageId) params.push(`ppid=${encodeURIComponent(productPageId)}`);
  return `https://apps.apple.com/app/apple-store/id${APP_STORE_ID}?${params.join('&')}`;
}

export function playStoreUrl(code, feature, source = 'familyring.io') {
  const referrer = `utm_source=${source}&utm_medium=web&utm_campaign=${code}&utm_content=${feature}`;
  return `https://play.google.com/store/apps/details?id=${encodeURIComponent(PLAY_PACKAGE)}&referrer=${encodeURIComponent(referrer)}`;
}

// Store links for a page. When the visitor came from an ad (utm_campaign in
// the address now, or remembered earlier in this visit), both links carry the
// ad's campaign code. Otherwise the page's own links are returned unchanged.
// feature: the page's feature code, used for utm_content in the Google Play
// referrer when the campaign code does not name one.
export function storeLinksFor({ search, appStore, playStore, feature = 'ii', storage = sessionStore() }) {
  const code = campaignFromSearch(search) || recallCampaign(storage);
  if (!code) return { code: null, appStore, playStore };
  return {
    code,
    appStore: appStoreUrl(code),
    playStore: playStoreUrl(code, featureOf(code, feature))
  };
}
